import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { type TFunction } from 'i18next';
import { use, useMemo } from 'react';

import { isAxiosError } from '@/api/util/isAxiosError.guard.ts';
import { redirectOnAuthFailure } from '@/api/util/redirectOnAuthFailure.util.ts';
import { DataOperationsErrorContext } from '@/errorHandling/DataOperationsErrorContext.ts';
import { captureAppException } from '@/monitoring/sentry.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

export const useGetQueryClient = (): QueryClient => {
  const { handleMinorError, handleOperationSuccess } = use(DataOperationsErrorContext);
  const { t } = useTranslationWithPrefix('generalError');
  return useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        mutations: {
          retry: false,
        },
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
        },
      },
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          if (mutation?.options?.meta?.disableErrorAlert) {
            return;
          }
          captureAppException(error, { contextName: 'react-query-mutation' });
          handleError(error, handleMinorError, t);
        },
        onSuccess: (_data, _variables, _context, mutation) => {
          if (mutation?.options?.meta?.disableSuccessAlert) {
            return;
          }
          handleOperationSuccess();
        },
      }),
      queryCache: new QueryCache({
        onError: (error: unknown) => {
          captureAppException(error, { contextName: 'react-query-query' });
          handleError(error, handleMinorError, t);
        },
        onSuccess: (_data, query) => {
          if (query?.options?.meta?.enableSuccessAlert) {
            handleOperationSuccess();
          }
        },
      }),
    });
  }, [handleMinorError, handleOperationSuccess, t]);
};

const handleError = (error: unknown, handleMinorError: (error: string) => void, t: TFunction) => {
  if (!isAxiosError(error)) {
    if (typeof error === 'string') {
      handleMinorError(error);
      return;
    }
    if (error instanceof Error) {
      handleMinorError(error.message);
      return;
    }
    handleMinorError('Unknown error occurred');
    return;
  }

  // Maintenance, 401 and 403 responses share their window-location handling
  // with the route guard loaders (OCD-309).
  if (
    redirectOnAuthFailure(error, {
      onMinorError: forbiddenStatus => handleMinorError(t(`forbidden.${forbiddenStatus}`)),
    })
  ) {
    return;
  }
  handleMinorError(typeof error.response?.data === 'string' ? error.response.data : error.message);
  throw error;
};
